import React, { FC } from "react";
import styled, { css } from "styled-components";
import { ConditionalWrapper } from "../ConditionalWrapper";
import { Icon, IconSvg } from "../Icon";
import { Tooltip, TooltipChildren } from "../Tooltip";
import { ProductIconType } from "../constants/productIcon";
import { style } from "../style";

export enum ResourceIconStatus {
  ACTIVE = "active",
  ONLINE = "online",
  OFF = "off",
  RUNNING = "running",
  ALERT = "alert",
  DOWN = "down",
  ERROR = "error",
  INVALID = "invalid",
  WARNING = "warning",
  RESIZING = "resizing",
  FAILOVER = "failover",
  DEGRADED = "degraded",
  DELETING = "deleting",
  NEW = "new",
  PROVISIONING = "provisioning",
  UPGRADING = "upgrading",
}

const isLoading = (status: string): boolean => {
  return status === ResourceIconStatus.NEW || status === ResourceIconStatus.PROVISIONING || status === ResourceIconStatus.UPGRADING;
};

const getResourceStatusColor = (status: string): string => {
  switch (status) {
    case ResourceIconStatus.ACTIVE:
    case ResourceIconStatus.ONLINE:
    case ResourceIconStatus.RUNNING:
      return "#15cd72";
    case ResourceIconStatus.ALERT:
    case ResourceIconStatus.DOWN:
    case ResourceIconStatus.ERROR:
    case ResourceIconStatus.INVALID:
      return style.colors.danger.base;
    case ResourceIconStatus.WARNING:
    case ResourceIconStatus.RESIZING:
    case ResourceIconStatus.FAILOVER:
    case ResourceIconStatus.DEGRADED:
    case ResourceIconStatus.DELETING:
      return style.colors.orange.light;
    case ResourceIconStatus.NEW:
    case ResourceIconStatus.PROVISIONING:
    case ResourceIconStatus.UPGRADING:
      return style.colors.white;
    case ResourceIconStatus.OFF:
      return style.colors.grey.primary;
    default:
      return style.colors.primary.base;
  }
};

const ICON_SIZE = 24;
const ICON_SIZE_LARGE = 48;
const CONTAINER_SIZE = ICON_SIZE + 14;
const CONTAINER_SIZE_LARGE = ICON_SIZE_LARGE + 8;

const ResourceIconContainer = styled.div<{
  $inline: boolean;
  $large: boolean;
}>`
  flex-shrink: 0;

  ${({ $inline, $large }) =>
    !$inline &&
    css`
      height: ${$large ? CONTAINER_SIZE_LARGE : CONTAINER_SIZE}px;
      width: ${$large ? CONTAINER_SIZE_LARGE : CONTAINER_SIZE}px;
    `}
`;

const IconWrapper = styled.div<{ $inline: boolean; $large: boolean }>`
  ${({ $inline }) =>
    $inline
      ? css`
          display: flex;
          flex-direction: row;
          align-items: center;
        `
      : css`
          position: absolute;
        `}

  ${IconSvg} {
    color: ${style.colors.blue.darkest};
    height: ${({ $large }) => ($large ? ICON_SIZE_LARGE : ICON_SIZE)}px;
    width: ${({ $large }) => ($large ? ICON_SIZE_LARGE : ICON_SIZE)}px;

    ${({ $inline }) =>
      !$inline &&
      css`
        padding: 6px;
      `}
  }
`;

const STATUS_INDICATOR_SIZE = 11;
const STATUS_INDICATOR_BORDER_WIDTH = 1;
const LOADING_SPINNER_SIZE = STATUS_INDICATOR_SIZE - 2 * STATUS_INDICATOR_BORDER_WIDTH;

const StatusIndicatorCircle = styled.div<{
  $status: string;
  $inline: boolean;
  $large: boolean;
}>`
  background-color: ${({ $status }) => getResourceStatusColor($status)};
  border: ${STATUS_INDICATOR_BORDER_WIDTH}px solid white;
  border-radius: 50%;
  height: ${STATUS_INDICATOR_SIZE}px;
  width: ${STATUS_INDICATOR_SIZE}px;

  ${({ $inline, $large }) =>
    $inline
      ? css`
          margin-right: ${style.vars.space["2"]};
        `
      : css`
          position: absolute;
          top: ${$large ? 40 : 23}px;
          right: ${$large ? 0 : -2}px;
        `}
`;

const LoadingSpinner = styled.div`
  width: ${LOADING_SPINNER_SIZE}px;
  height: ${LOADING_SPINNER_SIZE}px;
  border-radius: 50%;
  border-style: solid;
  border-color: #0069ff #0069ff transparent;
  border-width: ${STATUS_INDICATOR_BORDER_WIDTH}px;
  transition: background 0.3s ease;
  animation: rotation 0.75s 0s linear infinite;

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
`;

type ResourceStatusIndicatorProps = {
  status: string;
  inline: boolean;
  large: boolean;
};

const ResourceIconStatusIndicator: FC<ResourceStatusIndicatorProps> = ({ status, inline, large }) => (
  <ConditionalWrapper
    condition={inline}
    wrapper={(children) => (
      <Tooltip content={status} placement="right">
        <TooltipChildren>{children}</TooltipChildren>
      </Tooltip>
    )}
  >
    <StatusIndicatorCircle $status={status} $inline={inline} $large={large}>
      {isLoading(status) && <LoadingSpinner />}
    </StatusIndicatorCircle>
  </ConditionalWrapper>
);

type ResourceIconProps = {
  icon: ProductIconType;
  status?: string;
  inline?: boolean;
  large?: boolean;
  className?: string;
};

export const ResourceIcon: FC<ResourceIconProps> = ({ icon, status, large = false, inline = false, className }) => (
  <ResourceIconContainer $inline={inline} $large={large}>
    <IconWrapper $inline={inline} $large={large} className={className}>
      {status && <ResourceIconStatusIndicator status={status.toLowerCase()} inline={inline} large={large} />}
      <Icon icon={`product-${icon}`} round={!inline} />
    </IconWrapper>
  </ResourceIconContainer>
);
